/** @jsxImportSource @emotion/react */
// import { css, useTheme } from "@emotion/react";
import { useLanguage } from "../LanguageContext";
import InformationText from "../components/MainComponents/Analyses/InformationText";
import { useEffect, useRef, useState } from "react";
import {
  deleteAnalysis,
  listAnalyses,
  patchAnalysis,
} from "../services/analysisService";
import AnalysisTable from "../components/MainComponents/Analyses/DashboardComponents/AnalysisTable";
import Loader from "../components/MainComponents/Loader/Loader";
import { useSearchParams, useParams  } from "react-router-dom";
import AnalysesHeader from "components/MainComponents/Analyses/FormComponents/AnalyseHeader";
import WelcomeCard from "components/MainComponents/Analyses/FormComponents/WelcomeCard";
import FormCard from "components/MainComponents/Analyses/FormComponents/Form";
import { useToast } from "@coreai/component-library";
import DeleteAnalysesDialog from "../components/MainComponents/Analyses/FormComponents/DeleteAnalysesDialog";
import EditAnalysisForm from "../components/MainComponents/Analyses/FormComponents/EditAnalysisForm";
import { sortingColumnsProps } from "../components/MainComponents/DataTable/DataTable";
import { listRulesets } from "services/rulesetService";

export default function HomePage() {
  const { t, setReRenderAnalysisList, reRenderAnalysisList } = useLanguage();
  const [analysis, setAnalysis] = useState<any>([]);
  const [newAnalysis, setNewAnalysis] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showAlertDialog, setShowAlertDialog] = useState(false);  
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const analysisIdRef = useRef<null | string>(null);
  const [isTableDataLoading, setIsTableDataLoading] = useState(false); 
  const [sortingColumns, setSortingColumns] = useState<sortingColumnsProps>({});
  const [pageNo, setPageNo] = useState(window?.analysisTable?.pageNumber || 1);
  const [rowsPerPage, setRowsPerPage] = useState(window?.analysisTable?.pageSize || 5);
  const prevSorting = useRef<object | null>(null);
  const firstRender = useRef(true);
  const isEmptyObject = (obj: unknown): boolean => {
    return obj !== null && typeof obj === "object" && Object.keys(obj).length === 0;
  };
  
  const _listAnalyses = ({ _sortingColumns }: { _sortingColumns: sortingColumnsProps }) => {
  return listRulesets(1, 100)
    .then((rulesetList) => {
      const _rulesetObj: { [key: string]: string } = {};
      rulesetList?.results.forEach((_ruleset: any) => {
        if (_ruleset.stage) {
          _rulesetObj[_ruleset?.name] = `${t(_ruleset?.front_name)} (${t(_ruleset.stage)})`;
        } else {
          _rulesetObj[_ruleset?.name] = t(_ruleset?.front_name);
        }
      });

      if (_sortingColumns?.["ruleset.name"]) {
        const _analysis = [...analysis];
        _analysis.map((data: any) => {
          data.ruleset_name = _rulesetObj[data?.ruleset?.name];
        });

        if (_sortingColumns["ruleset.name"] === "asc") {
          _analysis.sort((a: any, b: any) =>
            a.ruleset_name > b.ruleset_name ? 1 : b.ruleset_name > a.ruleset_name ? -1 : 0
          );
        } else if (_sortingColumns["ruleset.name"] === "desc") {
          _analysis.sort((a: any, b: any) =>
            a.ruleset_name < b.ruleset_name ? 1 : b.ruleset_name < a.ruleset_name ? -1 : 0
          );
        }

        return { results: _analysis };
      }

      return listAnalyses({ sortingColumns: _sortingColumns });
    })
    .catch((error) => {
      console.error("Error list analyses", error);
      return { results: [] };
    });
};


  const { id } = useParams(); // Extracts "id" from the URL

  useEffect(() => {
  if (reRenderAnalysisList) {
    setIsTableDataLoading(true);
    _listAnalyses({ _sortingColumns: sortingColumns })
      .then((data) => setAnalysis(data.results))
      .finally(() => setIsTableDataLoading(false));

    setReRenderAnalysisList(false);
  }
}, [reRenderAnalysisList]);

   
  useEffect(() => {  
    // Only update if sortingColumns has actually changed
    if(isEmptyObject(sortingColumns)){
      return;
    }
    if (JSON.stringify(prevSorting.current) === JSON.stringify(sortingColumns)) {
      return;
    }  
    setIsTableDataLoading(true);  
    _listAnalyses({ _sortingColumns: sortingColumns })
      .then((data) => setAnalysis(data.results))
      .finally(() => setIsTableDataLoading(false));  
    // Update previous sorting state
    prevSorting.current = sortingColumns;
  }, [sortingColumns]);

  const onDelete = (analysisId: string) => {
  setIsLoading(true);

  deleteAnalysis(analysisId)
    .then(() => {
       toast({
        variant: 'success',
        title: t("analysis_toast_delete_success"),
      });
      return _listAnalyses({ _sortingColumns: sortingColumns });
    })
    .then((data) => {
      setAnalysis(data.results);     
      setReRenderAnalysisList(true);
    })
    .catch((error) => {
      console.error("Error deleting analysis:", error);
    })
    .finally(() => {
      setIsLoading(false);
    });
};

  const onEdit =  (analysisId: string, updatedAnalysis: any) => {
    setIsLoading(true);
      patchAnalysis(analysisId, updatedAnalysis).then(()=>{
        toast({
          variant: 'success',
          title: t("analysis_toast_edit_success"),
        });
        return _listAnalyses({ _sortingColumns: sortingColumns });
      }).then((data) => setAnalysis(data.results)).catch((error)=>{
          console.error("Error updating analysis:", error);
      }).finally(() => setIsLoading(false));   
    
  };

  const onCloseDialog = () => {
    setShowAlertDialog(false);
  };

  const onDeleteOpenDialog = (analysisId: string) => {
    analysisIdRef.current = analysisId;
    setShowAlertDialog(true);
  };
  const onPageChange = (page: number) => {
    setPageNo(page);
  };
  let contentToRender;
  if (isLoading) {
    contentToRender = <Loader />;
  } else if (id) {
    contentToRender = <EditAnalysisForm />;    
  // } else if (newAnalysis) {
  //   contentToRender = <FormCard />;
  } else if (analysis.length > 0) {
    contentToRender = (
      <AnalysisTable
        analysis={analysis}
        onDelete={onDeleteOpenDialog}
        onEdit={onEdit}
        sortingColumns={{...sortingColumns}}
        setSortingColumns={setSortingColumns}
        isTableDataLoading={isTableDataLoading}
        setPageNo={onPageChange}
        pageNo={pageNo}
        setRowsPerPage={setRowsPerPage}
        rowsPerPage={rowsPerPage}
      />
    );
  } else {
    contentToRender = <WelcomeCard setNewAnalysis={setNewAnalysis} />;
  }
  const isnewAnalysis = searchParams.get("newAnalysis");

  if (isnewAnalysis === "true") {
    contentToRender = <FormCard />;
  }

  useEffect(() => {
    setIsLoading(true);
    if (firstRender.current) {
      firstRender.current = false;
      listAnalyses({ sortingColumns })
      .then((data) => setAnalysis(data.results))
      .catch((error) => {})
      .finally(() => setIsLoading(false));
      return;
    }
   
  }, []);

  return (
    <>
      <div>
        <AnalysesHeader
          setNewAnalysis={setNewAnalysis}
          showSeeAllLink={!!(newAnalysis|| id)}
          totalAnalyses={analysis?.length}
        />
        {contentToRender}
        <InformationText />
          <DeleteAnalysesDialog
                analysis={analysis.find((analysis:any) => analysis.id === analysisIdRef.current)}
                open={showAlertDialog}
                onClose={onCloseDialog}
                onDelete={onDelete}
              />
      </div>
    </>
  );
}
